// filepath: apps/web/src/features/cabinet/chrome/Breadcrumbs.tsx
//
// FR-UI-CABINET-2 AC-11 — depth-aware breadcrumbs row.
//
// Hidden on routes with depth < 2 (e.g. "/" renders only the page
// title, no breadcrumb trail). For deep routes, each crumb except
// the last is a clickable link that navigates via the existing
// `navigate()` helper (no full page reload, no react-router-dom).
//
// v1: hard-coded route map for the 3 known deep routes
// (processes/instances/failed and processes/instances/:id/trace).
// When a new deep route is added, extend the matcher table.

import React, { useMemo } from "react";
import { useTenant } from "@/tenant/TenantProvider";
import { NEUTRAL, SPACE } from "../styles";
import { navigate } from "../usePathname";

export interface Breadcrumb {
  /** Visible label (plain text). */
  label: string;
  /** Path to navigate to (omit for the terminal crumb). */
  href?: string;
}

export function Breadcrumbs(): React.ReactElement | null {
  const pathname = usePathnameSafe();
  const { tenantSlug } = useTenant();
  const crumbs = useMemo(() => resolveCrumbs(pathname), [pathname]);

  if (crumbs.length < 2) return null;
  void tenantSlug; // INV-1 cache-key tenant-scoping identity (no fetch yet)
  const lastCrumb = crumbs[crumbs.length - 1];
  if (!lastCrumb) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumbs-nav"
      style={{
        fontSize: "13px",
        fontFamily: "system-ui, sans-serif",
        marginBottom: SPACE[3],
      }}
    >
      <ol
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexWrap: "wrap",
        }}
      >
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li
              key={`${c.label}-${idx}`}
              aria-current={isLast ? "page" : undefined}
              data-testid={isLast ? "crumb-current" : `crumb-${idx}`}
              style={{
                color: isLast ? NEUTRAL.textPrimary : NEUTRAL.textTertiary,
                fontWeight: isLast ? 600 : 400,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {isLast || !c.href ? (
                <span>{c.label}</span>
              ) : (
                <a
                  href={c.href}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(c.href as string);
                  }}
                  style={{
                    color: "#2563eb",
                    textDecoration: "none",
                  }}
                >
                  {c.label}
                </a>
              )}
              {!isLast ? (
                <span
                  aria-hidden="true"
                  style={{ color: NEUTRAL.textTertiary }}
                >
                  /
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// We can't import usePathname into this module without risking a
// circular import (usePathname lives in this folder's parent
// package). Re-exported via a local re-export so the component file
// stays self-contained.
import { usePathname } from "../usePathname";
function usePathnameSafe(): string {
  return usePathname();
}

interface CrumbResolver {
  match: (path: string) => Breadcrumb[] | null;
}

const RESOLVERS: CrumbResolver[] = [
  {
    // /processes/instances/:id/trace — Processes / Failed instances / {short-id}
    match: (path) => {
      const m = /^\/processes\/instances\/([^/]+)\/trace$/.exec(path);
      const idShort = m?.[1]?.slice(0, 8);
      if (!idShort) return null;
      return [
        { label: "Processes", href: "/processes/instances/failed" },
        { label: "Failed instances", href: "/processes/instances/failed" },
        { label: idShort },
      ];
    },
  },
  {
    // /processes/instances/failed
    match: (path) => {
      if (path !== "/processes/instances/failed") return null;
      return [
        { label: "Processes", href: "/processes/instances/failed" },
        { label: "Failed instances" },
      ];
    },
  },
];

function resolveCrumbs(path: string): Breadcrumb[] {
  for (const r of RESOLVERS) {
    const crumbs = r.match(path);
    if (crumbs !== null) return crumbs;
  }
  // Default for shallow routes: just a single "Cabinet" crumb so the
  // component renders nothing (depth < 2).
  return [{ label: "Cabinet" }];
}
